/**
 * services/chatApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Live chat support methods (user-facing and admin).
 *
 * METHODS:
 *   sendChatMessage(message, token)             — send a support chat message
 *   getChatHistory(token)                       — fetch chat history
 *   adminGetChatSessions(token)                 — list all chat sessions (admin)
 *   adminGetChatSessionMessages(sessionId, t)   — messages in a session (admin)
 *   adminEscalateChatSession(sessionId, token)  — escalate to human agent
 *   adminCloseChatSession(sessionId, token)     — close a resolved session
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async sendChatMessage(message, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/chat/message/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${t}` },
        body: JSON.stringify({ message }),
      });
      const data = await res.json();
      return res.ok ? data : { success: false, error: data.error || 'Failed' };
    } catch (e) { return { success: false, error: e.message }; }
  },

  async getChatHistory(token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/chat/history/`, {
        headers: { 'Authorization': `Token ${t}` },
      });
      const data = await res.json();
      return res.ok ? data : { success: false, messages: [] };
    } catch (e) { return { success: false, messages: [] }; }
  },

  async adminGetChatSessions(token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/admin/chat/sessions/`, {
        headers: { 'Authorization': `Token ${t}` },
      });
      const data = await res.json();
      return res.ok ? data : { success: false, sessions: [] };
    } catch (e) { return { success: false, sessions: [] }; }
  },

  async adminGetChatSessionMessages(sessionId, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/admin/chat/sessions/${sessionId}/messages/`, {
        headers: { 'Authorization': `Token ${t}` },
      });
      const data = await res.json();
      return res.ok ? data : { success: false, messages: [] };
    } catch (e) { return { success: false, messages: [] }; }
  },

  async adminEscalateChatSession(sessionId, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/admin/chat/sessions/${sessionId}/escalate/`, {
        method: 'POST', headers: { 'Authorization': `Token ${t}` },
      });
      return await res.json();
    } catch (e) { return { success: false }; }
  },

  async adminCloseChatSession(sessionId, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/admin/chat/sessions/${sessionId}/close/`, {
        method: 'POST', headers: { 'Authorization': `Token ${t}` },
      });
      return await res.json();
    } catch (e) { return { success: false }; }
  }

  // ✅ REFERRAL SYSTEM
  // All endpoints require an authenticated token.
  // Verified users only — backend returns 403 if not verified.

  /**
   * GET /api/referrals/
   * Returns the referrer's stats card data + unique referral link.
   * Auto-creates a Referral profile on first call for verified users.
   * Response: {
   *   referral_code, referral_link,
   *   total_referrals, active_referrals,
   *   total_earnings, pending_balance, withdrawn_total
   * }**/
};

